import React, { useState, ReactNode } from 'react';
import { useAuth0, useUserAuth, handleLogout } from '@lucid/auth';
import Sidebar from '../Sidebar/Sidebar';
import styles from './Layout.module.scss';

interface LayoutProps {
  children?: ReactNode;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
  showRoleDropdown?: boolean;
  currentPath?: string;
  onLocaleChange?: (locale: string) => void;
  /** Controlled collapsed state for the sidebar */
  isSidebarCollapsed?: boolean;
  /** Callback when sidebar collapsed state changes */
  onSidebarCollapseChange?: (collapsed: boolean) => void;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  theme = 'dark',
  onToggleTheme,
  showRoleDropdown = false,
  currentPath,
  onLocaleChange,
  isSidebarCollapsed: isSidebarCollapsedProp,
  onSidebarCollapseChange,
}) => {
  const { logout } = useAuth0();
  const { user } = useUserAuth();
  const [internalCollapsed, setInternalCollapsed] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isSidebarCollapsed = isSidebarCollapsedProp ?? internalCollapsed;

  const handleCollapseChange = (collapsed: boolean) => {
    if (onSidebarCollapseChange) {
      onSidebarCollapseChange(collapsed);
    } else {
      setInternalCollapsed(collapsed);
    }
  };

  const onSignOut = () => {
    handleLogout(logout);
  };

  return (
    <div className={styles.layout} data-theme={theme}>
      <Sidebar
        theme={theme}
        userName={user?.name as string | undefined}
        userEmail={user?.email as string | undefined}
        onToggleTheme={onToggleTheme}
        isCollapsed={isSidebarCollapsed}
        onCollapseChange={handleCollapseChange}
        onSignOut={onSignOut}
        showRoleDropdown={showRoleDropdown}
        currentPath={currentPath}
        onLocaleChange={onLocaleChange}
      />
      <main
        className={`${styles.main} ${isSidebarCollapsed ? styles.mainWithCollapsedSidebar : ''}`}
        data-theme={theme}
      >
        <div className={styles.content}>{children}</div>
      </main>
    </div>
  );
};

Layout.displayName = 'Layout';

export default Layout;
